import { Injectable } from "@angular/core";
import { BehaviorSubject, lastValueFrom } from "rxjs";
import { AbstractControl } from "@angular/forms";

import { GtmService } from "../analytics/gtm.service";
import { LocalStorageService } from "../util/local-storage.service";
import { StorageService } from "@core/services/util/storage.service";
import { AddressApiService } from "@app/core/http/address-api.service";

import { LatLng } from "@app/shared/models/util";
import { ServiceType } from "@app/shared/enums/service-type.enum";
import { AddressType, CustomerAddress } from "@app/shared/models/interfaces/customer-address";
import { CustomerAddressesResponse } from "@app/shared/models/api-response/customer-address";

@Injectable({
    providedIn: "root"
})
export class AddressService {
    addresses = new BehaviorSubject<CustomerAddress[]>([]);
    addressTypes = ["home", "work", "others"];
    outletLocality = new BehaviorSubject({});

    constructor(
        private gtmService: GtmService,
        private storageService: StorageService,
        private localStorage: LocalStorageService,
        private addressApiService: AddressApiService
    ) {
        const customerAddress = this.storageService.get("session-id") && this.localStorage.get("addresses_v2");

        if (customerAddress) {
            const addresses = this.localStorage.get("addresses_v2") || [];
            this.addresses.next(addresses);
        }

        // remove previous addresses field from local storage
        this.localStorage.remove("addresses");
    }

    fetch() {
        const sessionId = this.storageService.get("session-id");
        if (!sessionId) return;

        lastValueFrom(this.addressApiService.fetch())
            .then((resp: CustomerAddressesResponse) => {
                this.hydrateAddress(resp.customer_addresses)
            }).catch((e) => console.log(e)); //incase of failure no address will be shown
    }

    create(
		address_type: number,
		address_line_hash: any,
		address_custom_name: string,
		address_form_name: string,
		locality: string,
		latlng: LatLng,
		polygon_id?: string
	) {
        const locality_json = this.convertLocationToJSON(locality, latlng);
		return this.addressApiService.create(address_type, address_line_hash, address_form_name, locality_json, address_custom_name, polygon_id);
    }

    update(
		address_type: number,
		address_line_hash: any,
		address_custom_name: string,
		address_form_name: string,
		locality: string,
		address_id: number,
		latlng: LatLng,
		polygon_id?: string
	) {
        const landmark_json = this.convertLandmarkToJSON();
        const locality_json = this.convertLocationToJSON(locality, latlng);
		return this.addressApiService.update(address_type, address_line_hash, address_form_name, address_custom_name, address_id, locality_json, landmark_json, polygon_id);
    }

    delete(address_id: number) {
        return this.addressApiService.delete(address_id);
    }

    getLatLng(addrObj: any) {
        if (addrObj.location) {
            // api data
            return addrObj.location;
        }

        // geocode data
        const lat = (typeof addrObj.geometry.location.lat === "number") ? addrObj.geometry.location.lat : addrObj.geometry.location.lat();
        const lng =  (typeof addrObj.geometry.location.lng === "number") ? addrObj.geometry.location.lng : addrObj.geometry.location.lng();
        return { lat, lng };
    }

    private convertLocationToJSON(locality: string, latlng: LatLng) {
        return JSON.stringify({
            line0: locality,
            line1: "",
            line2: "",
            accurate: true,
            location: latlng
        });
    }

    private convertLandmarkToJSON() {
        return JSON.stringify({
            line0: "",
            line1: "",
            line2: "",
            accurate: false,
            location: {
                lat: 0.0,
                lng: 0.0
            }
        });
    }

    hydrateAddress(addresses: CustomerAddress[]) {

        addresses.sort((a: CustomerAddress, b: CustomerAddress) =>
            AddressType[a.address_type] > AddressType[b.address_type] ? 1 : -1
        );

        addresses.map((address: CustomerAddress) => {

            let addressLine = address.address_line_hash
                .slice(0, 2) // Take the first two hash from the array
                .map((item: any) => item[Object.keys(item)[0]])
                .filter((value: string | null) => Boolean(value))
                .join(", ");

            addressLine += ", " + address.formatted_locality;
            address.address_line = addressLine.trim();
        });   
        
        this.addresses.next(addresses);
        this.localStorage.set("addresses_v2", addresses);
    }

    getAvailableAddressTypes(): string[] {
        const savedAddressTypes = this.addresses.value
            .filter((address) => Boolean(address.address_type))
            .map((address) => address.address_type.toLowerCase());

        return this.addressTypes.filter(
            (type) => savedAddressTypes.indexOf(type.toLowerCase()) === -1
        );
    }

    setLocality(outlet_service_types: any[] = null) {
        
        let addressType = this.storageService.get("address_type") || null; 
        let addressName = '';
        const defaultName = "DELIVER AT"; 
        let locality = this.storageService.get("outlet_locality") || "Showing Default Location";
        
        const addressId = this.storageService.get("address_id") ?? null; 
    
        if (addressId) {
            const outlet_address = this.storageService.get("outlet_address");

            if (addressId == -1 || addressId == -2) {
                locality = outlet_address || locality;
                addressType = addressId == -2 ? 'Dine-in' : 'Takeaway';
            } else {
                const userAddress = this.addresses.value.find(address => Boolean(address.id == addressId));
    
                if (userAddress) {
                    const { address_custom_name, address_type, address_line } = userAddress;
                    addressName = address_custom_name || address_type;
                    addressType = 'Delivery';
                    locality = address_line;
                }
            }
        }
    
        const findServiceType = (code: string) => outlet_service_types?.find(type => Boolean(type.code === code));
    
        // available service types
        const deliveryType = findServiceType(ServiceType.DELIVERY);
        const dineInType = findServiceType(ServiceType.DINEIN);

        const serviceType = deliveryType || dineInType;

        addressType = addressType || serviceType?.name || defaultName; //priority -> saved address selected, outlet service type, default name
        this.outletLocality.next({
            locality,
            address_type: addressType,
            address_name: addressName
        });
    }

    noDoublePipeValidator(control: AbstractControl): { [key: string]: boolean } | null {
        const customName = control.value as string;
        const regex = /\|\|/; // Regex to match double pipe (||)
      
        if (regex.test(customName)) {
            this.gtmService.logWebAnalyticsEvent("address", "Address Field With Double Pipe");
            return { noDoublePipe: true };
        }
      
        return null;
    }

    getOrderServiceType(addressId: number) {
        if (addressId === -1) {
            return ServiceType.TAKEAWAY;
        }

        if (addressId === -2) {
            return ServiceType.DINEIN;
        }

        return ServiceType.DELIVERY;
    }

    noWhitespaceValidator(control: AbstractControl): { [key: string]: boolean } | null {
        return (control.value || '').trim().length ? null : { noWhiteSpace : true };       
    }
}
